
import React, { useEffect, useState, useMemo } from "react";
import { FaUserCircle } from "react-icons/fa";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { HueContext } from "../HueContext";
import { getAccountAnalytics } from "../api/analyticsApi";
import { endSpectraSession } from "../utils/session";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

export default function Settings() {
  const navigate = useNavigate();
  // ---------------- SETTINGS FORM STATE (kept) ----------------
  const [email, setEmail] = useState("");
  const [notifications, setNotifications] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const hue = useContext(HueContext);
  const [availableFunds, setAvailableFunds] = useState(0);
  const [executedOrders, setExecutedOrders] = useState(0);  



  // ---------------- CHART STATE (from Profile.jsx) ----------------
  const equityChartData = useMemo(() => ({
  labels: ["Jan", "Feb", "Mar", "Apr", "May"],
  datasets: [
    {
      label: "Equity",
      data: [1000, 1200, 900, 1400, 1600],
      borderColor: `hsl(${hue}, 100%, 60%)`,
      backgroundColor: `hsla(${hue}, 100%, 60%, 0.2)`,
      tension: 0.4,
      fill: true,
      pointRadius: 5,
    },
  ],
}), [hue]);

const plChartData = useMemo(() => ({
  labels: ["Jan", "Feb", "Mar", "Apr", "May"],
  datasets: [
    {
      label: "P/L %",
      data: [5, 10, -3, 12, 8],
      borderColor: `hsl(${hue}, 100%, 60%)`,
      backgroundColor: `hsla(${hue}, 100%, 60%, 0.2)`,
      tension: 0.3,
      fill: true,
      pointRadius: 5,
    },
  ],
}), [hue]);

  const [allocationData, setAllocationData] = useState({
    labels: ["Physical Allocation", "Coin Value", "Spectra Cash"],
    datasets: [
      {
        label: "Portfolio Allocation",
        data: [50, 30, 20],
        backgroundColor: ["#ffd700", "#00bfff", "#22bf2fc8"],
        borderWidth: 1,
      },
    ],
  });

  // ---------------- USER INFO (from Profile.jsx) ----------------
      const username = localStorage.getItem("spectraUser");
  const bankLinked = localStorage.getItem(`bankLinked_${username}`) === "true";
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const user = {
  username: username || "Unknown User",
  email: userEmail || "—",
  timezone: timezone,
  accountStatus: bankLinked ? "Active" : "Pending",
  lastLogin: new Date().toLocaleString(),
  totalInvestments: `$${availableFunds.toLocaleString()}`,
  transactionsCount: executedOrders,
};

  const extraStats = [
    { title: "Daily High", value: "$15.20" },
    { title: "Daily Low", value: "$14.30" },
    { title: "Other Coin Stats", value: "$8,540" },
  ];

  // Pull saved dashboard data if present (same behavior as Profile.jsx)
  useEffect(() => {
    const savedData = JSON.parse(localStorage.getItem("dashboardData"));
    if (savedData) {
      setEquityChartData(savedData.equityChartData || equityChartData);
      setPlChartData(savedData.plChartData || plChartData);
      setAllocationData(savedData.allocationData || allocationData);
    }
   
  }, []);


    useEffect(() => {
  async function loadAccount() {
    try {
      const data = await getAccountAnalytics();
      setAvailableFunds(Number(data.cash));
    } catch (err) {
      console.error("Failed to load account", err);
    }
  }

  loadAccount();
}, []);

useEffect(() => {
  const storedUser = localStorage.getItem("spectraUser");
  if (!storedUser) return;
  async function loadUserInfo() {
    try {
      const token = localStorage.getItem("spectraToken") || "";
      const res = await fetch(`${process.env.REACT_APP_SGMA_API_URL}/api/user/${storedUser}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.email) {
        setUserEmail(data.email);
        setEmail(data.email);
      }
    } catch (err) {
      console.error("Failed to load user info", err);
    }
  }
  loadUserInfo();
}, []);




useEffect(() => {
  const stored = JSON.parse(
    localStorage.getItem("spectraTransactions") || "[]"
  );

  setExecutedOrders(stored.length);
}, []);



  return (
    <div
      style={{
        padding: "40px 24px",
        maxWidth: "1000px",
        margin: "0 auto",
        fontFamily: "Arial, sans-serif",
        background: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <h1 style={{ textAlign: "center", marginBottom: "40px", color: "#333" }}>
        Settings
      </h1>

      {/* ================= USER INFO SECTION ================= */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "24px",
          marginBottom: "40px",
          background: "#fff",
          padding: "24px",
          borderRadius: "16px",
          boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
          flexWrap: "wrap",
        }}
      >
           <FaUserCircle
  size={100}
  color={`hsl(${hue}, 100%, 60%)`}
  style={{
    filter: `drop-shadow(0 0 12px hsl(${hue}, 100%, 60%))`,
    transition: "filter 0.1s linear"
  }}
/>


        <div style={{ fontSize: "16px", color: "#555" }}>
          <p>
            <strong>Username:</strong> {user.username}
          </p>
          <p>
            <strong>Email:</strong> {user.email}
          </p>
          <p>
            <strong>Timezone:</strong> {user.timezone}
          </p>
          <p>
            <strong>Account Status:</strong> {user.accountStatus}
          </p>
          <p>
            <strong>Last Login:</strong> {user.lastLogin}
          </p>
        </div>
      </div>

      {/* ================= ACCOUNT STATS ================= */}
      <div style={{ display: "flex", gap: "24px", marginBottom: "40px", flexWrap: "wrap" }}>
        <div style={statCardStyle}>
           <h3>Available Funds</h3>
          <p style={statNumberStyle}>{user.totalInvestments}</p>
        </div>

        <div style={statCardStyle}>
            <h3>Executed Orders</h3>
          <p style={statNumberStyle}>{user.transactionsCount}</p>
        </div>
      </div>

      {/* ================= ANALYTICS SECTION ================= */}
      <div
        style={{
          background: "#fff",
          padding: "24px",
          borderRadius: "16px",
          boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
          marginBottom: "40px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <h2 style={{ margin: 0 }}>Portfolio History</h2>
        
        </div>

        <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
          {/* Equity Chart */}
          <div style={{ flex: "1 1 400px", minHeight: "300px" }}>
            <Line data={equityChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>

          {/* P/L Chart */}
          <div style={{ flex: "1 1 400px", minHeight: "300px" }}>
            <Line data={plChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>

          {/* Pie Chart */}
          <div style={{ flex: "1 1 400px", minHeight: "300px" }}>
            <Pie data={allocationData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </div>

        {/* Extra Stats Boxes */}
        <div style={{ display: "flex", gap: "16px", marginTop: "24px", flexWrap: "wrap" }}>
          {extraStats.map((s, i) => (
            <div key={i} style={statCardStyle}>
              <h4 style={{ marginTop: 0 }}>{s.title}</h4>
              <p style={statNumberStyle}>{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ================= BOTTOM: Settings Form ABOVE Buttons ================= */}
      <div
        style={{
          background: "#fff",
          padding: 24,
          borderRadius: 16,
          boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
          maxWidth: 650,
          margin: "0 auto",
        }}
      >
        {/* Settings form (top) */}
        <h2 style={{ marginTop: 0, marginBottom: 16 }}>Account Settings</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 8 }}>Email Address</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              width: "100%",
              padding: 12,
              borderRadius: 6,
              border: "1px solid #ccc",
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label>
            <input
              type="checkbox"
              checked={notifications}
              onChange={() => setNotifications(!notifications)}
              style={{ marginRight: 8 }}
            />
            Enable notifications
          </label>
        </div>

        {/* Buttons (directly under settings form) */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 12 }}>
          <button style={buttonStyle} onClick={() => navigate("/profile/edit")}>
            Edit Profile
          </button>

          <button style={buttonStyle} onClick={() => navigate("/profile/password")}>
            Change Password
          </button>

          <button style={buttonStyle} onClick={() => navigate("/support")}>
            Contact and Support
          </button>

          <button
            style={{ ...buttonStyle, background: "#e40000df", color: "#fff" }}
            onClick={endSpectraSession}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- STYLES ---------------- */

const statCardStyle = {
  flex: "1 1 200px",
  background: "#fff",
  padding: "24px",
  borderRadius: "16px",
  textAlign: "center",
  boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
  minWidth: "150px",
};

const statNumberStyle = {
  fontSize: "28px",
  fontWeight: "bold",
  color: "#333",
  marginTop: "12px",
};

const buttonStyle = {
  background: "#FFD700",
  color: "#111",
  padding: "12px 18px",
  borderRadius: 10,
  border: "none",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 15,
};
